import React from 'react';
import { Select, MenuItem, styled } from '@mui/material';
import { ExpandMore, Check } from '@mui/icons-material';
import { Colors } from 'library';

type RSelectTone = 'primary' | 'muted' | 'error';

interface RSelectOption {
    key: string | number;
    value: string;
}

interface RSelectProps {
    value: string | number;
    onChange: (value: string) => void;
    options: RSelectOption[];
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    tone?: RSelectTone;
}

const TONE_BG: Record<RSelectTone, string> = {
    primary: Colors.white,
    muted: Colors.faintGrey,
    error: Colors.lightRed,
};

const TONE_BORDER: Record<RSelectTone, string> = {
    primary: Colors.lightGrey,
    muted: Colors.lightGrey,
    error: Colors.mediumRed,
};

const TONE_FOCUS_BORDER: Record<RSelectTone, string> = {
    primary: Colors.mediumGrey,
    muted: Colors.mediumGrey,
    error: Colors.mediumRed,
};

export function RSelect({ value, onChange, options, placeholder, disabled, fullWidth, tone = 'muted' }: RSelectProps) {
    return (
        <StyledSelect
            value={value}
            onChange={(e) => onChange(e.target.value as string)}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty={!!placeholder}
            IconComponent={StyledExpandMore}
            $tone={tone}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                    },
                },
            }}
            renderValue={(selected) => {
                if (placeholder && !selected) return <Placeholder>{placeholder}</Placeholder>;
                return options.find((o) => String(o.key) === String(selected))?.value || '';
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key} selected={String(opt.key) === String(value)}>
                    {String(opt.key) === String(value) && <Check sx={{ fontSize: 18, marginRight: '8px' }} />}
                    {opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.mediumGrey,
});

const StyledSelect = styled(Select, { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: RSelectTone }>(
    ({ $tone }) => ({
        fontSize: 14,
        color: Colors.darkNavy,
        backgroundColor: TONE_BG[$tone],
        borderRadius: 10,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_FOCUS_BORDER[$tone],
            borderWidth: 1,
        },
        '&.Mui-disabled': {
            backgroundColor: Colors.lightGrey,
            color: Colors.mediumGrey,
        },
        '& .MuiSelect-select': {
            padding: '10px 14px',
        },
    }),
);

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '7px 14px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.darkNavy,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
    '&:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
});

const Placeholder = styled('span')({
    color: Colors.lightGrey,
});
